'use client';

import { memo, useEffect } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { PAGE_TYPE } from '@/app/common/pageType';
import { hasSlowConnection, loadBiff } from '@/app/common/biff/biffLoader.ts';
import { installHotelDetailBanners } from './biffHotelDetailPage.ts';

/**
 * Hooks the hotel detail page into biff. The script itself is booted page-wide by BiffBootstrap and gates
 * on consent on its own; here we only request this page's ad configuration once its visit event has reached
 * platformData (biff reads page.section etc. from it) and, once that configuration's slots are defined,
 * register the detail ad slots. Registering them earlier would run against the results page's slots after
 * a client-side results → detail navigation, and biff drops ids it does not know.
 *
 * As before biff 8, the detail page shows no ads on a slow connection or with data-saver on (the search page
 * has no such rule).
 */
const HotelDetailBiff = () => {
  const { commonData, localization } = usePageWrapperData();
  const isLoggedIn = !!commonData?.authResponse?.loggedIn;
  // MPH-1274: page-independent, so reading it once on mount is safe.
  const withBottomAds = !localization?.configValuesApp?.hideDetailBottomAds;

  // Mount-only on purpose: one configuration request and one slot registration per page visit.
  useEffect(() => {
    if (hasSlowConnection()) return undefined;

    const abortController = new AbortController();

    loadBiff({
      isLoggedIn,
      key: PAGE_TYPE.HOTEL_DETAIL,
      fromIndex: window.platformData?.length ?? 0,
      signal: abortController.signal,
    }).then((loaded) => {
      if (loaded && !abortController.signal.aborted) installHotelDetailBanners({ withBottomAds });
    });

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default memo(HotelDetailBiff);
